import { DateFormats, formatDate, formatNameLastFirst } from '@strata/helper-functions';
import { ActionButtonPageTitle, ActionButtonText, Button } from '@strata/react-buttons';
import { ComparisonColumns } from '@strata/react-comparison-columns';
import { WarningDialog } from '@strata/react-dialog';
import { FormHeaderAndContent } from '@strata/react-form-header-and-content';
import { InlineMessage } from '@strata/react-inline-message';
import { Spinner } from '@strata/react-loading-animation';
import { SuccessToast } from '@strata/react-toast-message';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { TreatmentMergeCaseStatus, TreatmentMergeCaseStatusIndicatorMap } from 'services/constants';
import { handleError } from 'services/Error';
import { useGoBack } from 'services/navigationHelpers';
import { AppRoutes } from 'services/routes';
import {
    useDismissTreatmentMergeCaseMutation,
    useGetTreatmentMergeCaseQuery,
    useGetTreatmentsDetailsQuery,
    useMergeTreatmentMergeCaseMutation,
} from 'store/api/treatmentApi';

const MERGE = 'merge';

const formatResolution = ({ status, resolvedBy, resolvedAt }) => {
    const resolvedOn = formatDate(resolvedAt, DateFormats.DATE_TIME);
    if (status === TreatmentMergeCaseStatus.UNMERGED) {
        return `The patients were unmerged on ${resolvedOn}, so the treatments were moved back automatically.`;
    }
    if (status === TreatmentMergeCaseStatus.SPLIT_REQUIRED) {
        return `These treatments were already merged by ${resolvedBy} on ${resolvedOn}, but the patients have since been unmerged. The treatment needs a manual split.`;
    }
    return `Resolved by ${resolvedBy} on ${resolvedOn}.`;
};

const rows = [
    { key: 'treatment', label: 'Treatment' },
    { key: 'patient', label: 'Patient' },
    { key: 'memberId', label: 'Member ID' },
    { key: 'dateOfBirth', label: 'DOB' },
    { key: 'product', label: 'Product' },
    { key: 'status', label: 'Status' },
    { key: 'treatmentStart', label: 'Treatment Start' },
    { key: 'activeReferral', label: 'Active Referral' },
    { key: 'referrals', label: 'Referrals' },
    { key: 'auths', label: 'Auths' },
    { key: 'keep', label: '' },
];

export const TreatmentMergeCaseDetailsPage = () => {
    const { payerId, caseId } = useParams();
    const navigate = useNavigate();
    const currentLocation = {
        route: AppRoutes.treatmentMergeCaseDetails(payerId, caseId),
        text: 'Treatment Merge Case',
    };
    const { backToDestination, goBack } = useGoBack({
        route: AppRoutes.treatmentMergeCases,
        text: 'Treatment Merge Queue',
    });

    const [confirming, setConfirming] = useState(null);
    const isMergeConfirm = confirming?.type === MERGE;

    const { data: mergeCase, isError: isCaseError } = useGetTreatmentMergeCaseQuery({
        payerId,
        caseId,
    });
    const { data: treatments, isError: isTreatmentsError } = useGetTreatmentsDetailsQuery(
        { payerId, treatmentIds: mergeCase?.treatmentIds },
        { skip: !mergeCase },
    );
    const [mergeTreatmentMergeCase, { isLoading: isMerging }] =
        useMergeTreatmentMergeCaseMutation();
    const [dismissTreatmentMergeCase, { isLoading: isDismissing }] =
        useDismissTreatmentMergeCaseMutation();

    const isPending = mergeCase?.status === TreatmentMergeCaseStatus.PENDING;
    const statusText =
        TreatmentMergeCaseStatusIndicatorMap[mergeCase?.status]?.message ?? mergeCase?.status;

    const handleConfirm = async () => {
        try {
            if (isMergeConfirm) {
                await mergeTreatmentMergeCase({
                    payerId,
                    caseId,
                    survivingTreatmentId: confirming.treatmentId,
                }).unwrap();
                SuccessToast({ message: 'Treatments merged.' });
            } else {
                await dismissTreatmentMergeCase({ payerId, caseId }).unwrap();
                SuccessToast({ message: 'Merge case dismissed.' });
            }
            setConfirming(null);
        } catch (error) {
            handleError('Unable to resolve the merge case. Please try again.', error);
        }
    };

    const columns = (treatments ?? []).map((treatment, index) => ({
        columnTitle:
            treatment.treatmentId === mergeCase?.survivingTreatmentId
                ? `Treatment ${index + 1} (Surviving)`
                : `Treatment ${index + 1}`,
        values: {
            treatment: (
                <ActionButtonText
                    label={treatment.treatmentId}
                    onClick={() =>
                        navigate(AppRoutes.treatmentDetails(payerId, treatment.treatmentId), {
                            state: { backToDestination: currentLocation },
                        })
                    }
                />
            ),
            patient: formatNameLastFirst(treatment.lastName, treatment.firstName),
            memberId: treatment.memberDetails.memberId,
            dateOfBirth: formatDate(treatment.memberDetails.dateOfBirth),
            product: treatment.productServiceOid,
            status: treatment.treatmentStatus,
            treatmentStart: formatDate(treatment.treatmentStart),
            activeReferral: treatment.activeReferral?.referralId ?? 'None',
            referrals: treatment.referralList.length,
            auths: treatment.authList.length,
            keep: isPending && (
                <Button
                    label="Keep This Treatment"
                    intent={Button.Intent.PRIMARY}
                    onClick={() =>
                        setConfirming({ type: MERGE, treatmentId: treatment.treatmentId })
                    }
                />
            ),
        },
    }));

    const renderComparison = () => {
        if (isCaseError || isTreatmentsError) {
            return (
                <InlineMessage
                    intent={InlineMessage.Intent.ERROR}
                    title="Unable to load the merge case"
                    label="Refresh the page to try again."
                />
            );
        }
        if (!treatments) {
            return <Spinner size={Spinner.Size.LARGE} intent={Spinner.Intent.NONE} />;
        }
        return <ComparisonColumns rows={rows} columns={columns} />;
    };

    return (
        <>
            <WarningDialog
                isOpen={confirming !== null}
                dialogTitle={isMergeConfirm ? 'Merge Treatments' : 'Dismiss Merge Case'}
                dialogContent={
                    <div className="padding__bottom--medium">
                        {isMergeConfirm
                            ? 'The other treatments will be discontinued and their auths and Rxs moved to the treatment you keep. This cannot be undone.'
                            : 'Every treatment will be kept as it is.'}
                    </div>
                }
                primaryButtonLabel={isMergeConfirm ? 'Yes, Merge' : 'Yes, Dismiss'}
                primaryButtonIntent={Button.Intent.DANGER}
                primaryButtonDisabled={isMerging || isDismissing}
                onPrimaryButtonClick={handleConfirm}
                secondaryButtonLabel="Cancel"
                onSecondaryButtonClick={() => setConfirming(null)}
            />
            <FormHeaderAndContent
                rowOneContentHeader={
                    <>
                        <ActionButtonPageTitle
                            label={backToDestination.text}
                            title={currentLocation.text}
                            secondaryTitle={mergeCase?.productServiceOid}
                            onClick={goBack}
                        />
                        {isPending && (
                            <div style={{ marginLeft: 'auto' }}>
                                <Button
                                    label="Dismiss"
                                    intent={Button.Intent.NONE}
                                    onClick={() => setConfirming({ type: 'dismiss' })}
                                />
                            </div>
                        )}
                    </>
                }
                mainContent={
                    <div className="padding__around--medium">
                        {mergeCase && !isPending && (
                            <div className="margin__bottom--medium">
                                <InlineMessage
                                    intent={
                                        mergeCase.status === TreatmentMergeCaseStatus.SPLIT_REQUIRED
                                            ? InlineMessage.Intent.WARNING
                                            : InlineMessage.Intent.INFO
                                    }
                                    title={`Case ${statusText.toLowerCase()}`}
                                    label={formatResolution(mergeCase)}
                                />
                            </div>
                        )}
                        {renderComparison()}
                    </div>
                }
            />
        </>
    );
};
